// src/components/PrayerDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { getCountryFlag, getCountryName } from '../utils/countries';
import { handleShare, getShareOptions } from '../utils/shareUtils';

// Funciones para manejar oraciones diarias (copiadas de Dashboard)
const getPrayedTodayKey = (prayerId) => `prayed_${prayerId}_${new Date().toDateString()}`;

const hasUserPrayedToday = (prayerId) => {
  const key = getPrayedTodayKey(prayerId);
  return localStorage.getItem(key) === 'true';
};

const markUserPrayedToday = (prayerId) => {
  const key = getPrayedTodayKey(prayerId);
  localStorage.setItem(key, 'true');
};

// Componente Modal de opciones de compartir (mismo que en Dashboard)
function ShareModal({ prayer, isOpen, onClose }) {
  const [copying, setCopying] = useState(null);
  
  if (!isOpen) return null;

  const shareOptions = getShareOptions().filter(option => option.available);
  const primaryOptions = shareOptions.filter(option => option.primary);
  const secondaryOptions = shareOptions.filter(option => !option.primary);

  const handleOptionClick = async (method) => {
    if (copying === method) return;

    setCopying(method);
    try {
      await handleShare(prayer, method);
      
      if (method === 'copy-link' || method === 'copy-text') {
        setTimeout(() => {
          setCopying(null);
        }, 1500);
      } else {
        setCopying(null);
        onClose();
      }
    } catch (error) {
      console.error('Error al compartir:', error);
      alert('Error al compartir. Inténtalo de nuevo.');
      setCopying(null);
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={e => e.stopPropagation()}>
        <div className="share-modal-header">
          <h3>Compartir pedido de oración</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <div className="share-modal-content">
          <div className="prayer-preview">
            <strong>{prayer.nombre}</strong>
            <span>{getCountryFlag(prayer.pais)} {getCountryName(prayer.pais)}</span>
            <p>"{prayer.comentario}"</p>
          </div>

          <div className="share-options">
            <div className="primary-options">
              {primaryOptions.map(option => (
                <button
                  key={option.id}
                  className={`share-option-button ${copying === option.id ? 'copying' : ''}`}
                  onClick={() => handleOptionClick(option.id)}
                  disabled={copying === option.id}
                >
                  <span className="option-icon">{option.icon}</span>
                  <span className="option-label">
                    {copying === option.id ? (
                      option.id.includes('copy') ? '¡Copiado!' : 'Compartiendo...'
                    ) : (
                      option.label
                    )}
                  </span>
                </button>
              ))}
            </div>

            {secondaryOptions.length > 0 && (
              <>
                <div className="share-divider">
                  <span>Más opciones</span>
                </div>
                <div className="secondary-options">
                  {secondaryOptions.map(option => (
                    <button
                      key={option.id}
                      className="share-option-button secondary"
                      onClick={() => handleOptionClick(option.id)}
                    >
                      <span className="option-icon">{option.icon}</span>
                      <span className="option-label">{option.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PrayerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prayer, setPrayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareModal, setShareModal] = useState({ isOpen: false, prayer: null });
  const [sharingPrayer, setSharingPrayer] = useState(null);

  useEffect(() => {
    fetchPrayer();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPrayer = async () => {
    try {
      setLoading(true);
      const apiUrl = `${config.API_BASE_URL}/api/prayers/${id}`;
      const response = await axios.get(apiUrl);
      setPrayer(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al cargar pedido:', error);
      setError('Pedido no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handlePrayForThis = async () => {
    if (hasUserPrayedToday(prayer.id)) {
      alert('Ya oraste por esta intención hoy. ¡Puedes orar de nuevo mañana!');
      return;
    }

    try {
      const apiUrl = `${config.API_BASE_URL}/api/prayers`;
      const response = await axios.patch(`${apiUrl}/${prayer.id}/pray`);
      if (response.data.success) {
        setPrayer(prev => ({
          ...prev,
          oraciones: response.data.oraciones
        }));
        
        setTimeout(() => {
          markUserPrayedToday(prayer.id);
          setPrayer(prev => ({ ...prev })); // Forzar re-render
        }, 1500);
      }
    } catch (error) {
      console.error('Error al rezar por este pedido:', error);
      alert('Error al registrar tu oración. Inténtalo de nuevo.');
    }
  };

  const handleShareClick = async () => {
    setSharingPrayer(prayer.id);
    
    try {
      const result = await handleShare(prayer, 'auto');
      
      if (result === 'show-options') {
        setShareModal({ isOpen: true, prayer });
      }
    } catch (error) {
      console.error('Error al compartir:', error);
      setShareModal({ isOpen: true, prayer });
    } finally {
      setSharingPrayer(null);
    }
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, prayer: null });
  };

  if (loading) {
    return (
      <div className="prayer-detail-container">
        <div className="loading">
          <h2>Cargando pedido de oración...</h2>
        </div>
      </div>
    );
  }

  if (error || !prayer) {
    return (
      <div className="prayer-detail-container">
        <div className="prayer-detail-error">
          <h2>Pedido no encontrado</h2>
          <p>El pedido de oración que buscas no existe o ha sido eliminado.</p>
          <button 
            className="back-button"
            onClick={() => navigate('/')}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="prayer-detail-container">
      <div className="prayer-detail-header">
        <button 
          className="back-button"
          onClick={() => navigate('/')}
        >
          ← Volver al inicio
        </button>
        <h1>Pedido de Oración</h1>
      </div>

      <div className="prayer-detail-card">
        <div className="prayer-header">
          <div className="prayer-author">
            <strong>{prayer.nombre}</strong>
            <div className="prayer-meta">
              <span>{getCountryFlag(prayer.pais)} {getCountryName(prayer.pais)}</span>
              <span>{prayer.fecha}</span>
              <span className={`category-badge category-${prayer.categoria?.toLowerCase()}`}>
                {prayer.categoria}
              </span>
            </div>
          </div>
        </div>

        <div className="prayer-content-detail">
          <p>"{prayer.comentario}"</p>
        </div>

        <div className="prayer-stats">
          <div className="prayer-count">
            <span className="count-number">{prayer.oraciones || 0}</span>
            <span className="count-label">
              {prayer.oraciones === 1 ? 'persona ha orado' : 'personas han orado'}
            </span>
          </div>
        </div>

        <div className="prayer-actions-detail">
          <button 
            className={`pray-button-detail ${hasUserPrayedToday(prayer.id) ? 'prayed-today' : ''}`}
            onClick={handlePrayForThis}
            disabled={hasUserPrayedToday(prayer.id)}
          >
            {hasUserPrayedToday(prayer.id) ? (
              <>Ya oraste hoy</>
            ) : (
              <>Orar por esto</>
            )}
          </button>
          
          <button 
            className={`share-button-detail ${sharingPrayer === prayer.id ? 'sharing' : ''}`}
            onClick={handleShareClick}
            disabled={sharingPrayer === prayer.id}
          >
            {sharingPrayer === prayer.id ? 'Compartiendo...' : 'Compartir'}
          </button>
        </div>

        <div className="prayer-call-to-action">
          <p>¿Tienes una intención especial?</p>
          <button 
            className="new-prayer-button"
            onClick={() => navigate('/nuevo-pedido')}
          >
            Enviar mi pedido de oración
          </button>
        </div>
      </div>

      <ShareModal 
        prayer={shareModal.prayer}
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
      />
    </div>
  );
}

export default PrayerDetail;